<script lang="ts">
	import type { IContextMenuItem } from "$lib/def/util-types";
	import { notifyOnClickOutside } from "$lib/util/ui";
	import { createEventDispatcher } from "svelte";

	/** The context menu's test id */
	export let id: string;
	export let menuItems: IContextMenuItem[];
	export let clickClientY: number; // i.e. relative to viewport
	let offsetY = 0; // i.e. relative to parent element

	const dispatch = createEventDispatcher();

	// "Modal behaviour": send a notice to hide the context menu when user clicks/
	// right-clicks (i.e. possibly opens another context menu elsewhere) outside it
	const hideOnClickOutside = (node: Node) => {
		return notifyOnClickOutside(node, () => { dispatch("hide"); }, true);
	};

	// Used for centering the menu element
	let menuElement: HTMLMenuElement;
	let menuWidth: number;
	$: if (menuElement) {
		menuWidth = menuElement.offsetWidth;
	}

	// Used to determine whether to open downwards or upwards
	let menuHeight: number;
	$: if (menuElement) menuHeight = menuElement.clientHeight;
	$: {
		if (typeof menuHeight !== 'undefined'
			&& typeof menuElement.parentElement !== 'undefined'
		) {
			let bottomOfMenuYPos = clickClientY + menuHeight; // relative to viewport

			let menuClientY: number;
			if (bottomOfMenuYPos > window.innerHeight) {
				// The menu needs to open upwards, so the top of the menu should be one menu height
				// above where the click happened
				menuClientY = clickClientY - menuHeight; // this is relative to the viewport
			} else {
				// There's enough space for the menu to open downwards, so we can use the click's
				// position as the top of the menu
				menuClientY = clickClientY;
			}

			// Now we need to get the menu's position relative to its parent
			const parentClientY = menuElement.parentElement!.getBoundingClientRect().top;
			offsetY = menuClientY - parentClientY;
	}
}

	

</script>

<menu
	class="shadow-elevation-24dp"
	style="--offsetY: {offsetY}px; --width: {menuWidth}px"
	use:hideOnClickOutside
	bind:this={menuElement}
	data-testid={id}
>
	{#each menuItems as {id: itemId, label}}
		<li class="label-xl medium">
			<button on:click|stopPropagation={() => dispatch("clicked", itemId)}>
				{label}</button>
		</li>
	{/each}
</menu>

<style lang="scss">
	@use "color";

	menu {
		// Show at least two items
		// 1. li > button height = label-xl max font size
		--buttonHeight: calc(1rem + 3px);
		// 2. li > button padding and menu padding = 12px
		--paddingY: calc(2 * 12px); // top and bottom
		// Therefore menu's max height [paddingY is used for menu and 2 buttons']-->
		max-height: calc(2 * var(--buttonHeight) + 3 * var(--paddingY));
		overflow-y: scroll;
		position: absolute;
		top: var(--offsetY);
		left: calc(-1 * (var(--width) - 100%) / 2);
		background-color: color.$background-elevation-12dp;
		border-radius: 24px;
		padding: 12px;
		min-width: 100%;
		max-width: fit-content;
		z-index: 3; // appears above tags e.g. combo, 18+, and sold out cover
		border: 1px solid color.$separator;
	}

	menu > li {
		list-style: none;
		margin: 0;
		width: 100%;
	}

	menu > li > button {
		background-color: unset;
		line-height: 1;
		color: color.$on-background-high-importance;
		border-radius: 12px;
		padding: 12px;
		width: 100%;
		text-align: left;

		&:hover {
			background-color: color.$object-elevation-3dp;
		}

		&:active {
			color: color.$background;
			background-color: color.$on-background-high-importance;
			border: 2px solid color.$separator;
		}
	}
</style>